import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import Quill from "quill";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import AceEditor from "react-ace";
import { Controller, useFormContext } from "react-hook-form";
import "ace-builds/src-noconflict/mode-html";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/ext-language_tools";
import "ace-builds/src-noconflict/ext-searchbox";

import {
  Button,
  Drawer,
  Input,
  Modal,
  SearchInput,
  TextArea,
} from "components";
import { LeftRightArrowIconChip } from "components/icons/LeftRightArrowIconChip";
import QuillToolBar from "components/quill/quillToolbar";
import { uploadEmailTemplateAttachment } from "controller";
import toast from "react-hot-toast";
import { AttachmentData } from "shared/features/attachment/attachmentType";
import { debugConsole, minifyHtml } from "utilz/helpers";
import { v4 as uuidv4 } from "uuid";
import {
  ChipBlot,
  HTMLCodeBlot,
  SignatureBlot,
  StyledImageBlot,
} from "./BlotComponents";
import DefaultToolbar from "./DefaultToolbarForQuil";
import quilStyle from "./quilStyle";

// Define the types
interface ChipOption {
  id: string;
  label: string;
  value: string;
  icon?: string;
  bgColor?: string;
  fontColor?: string;
}

// Add new interface for HtmlCodeModal props
interface HtmlCodeModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  primaryButtonText: string;
  secondaryButtonText: string;
  onPrimaryButtonClick: () => void;
  onSecondaryButtonClick: () => void;
  htmlCode: string;
  onHtmlCodeChange: (code: string) => void;
  chipOptions: ChipOption[];
  onChipInsert: (chip: ChipOption) => void;
  sx?: any;
}

interface CustomQuillEditorProps {
  name?: string; // Required for controlled usage with react-hook-form
  value?: string; // For standalone usage
  onChange?: (content: string, delta: any, source: string, editor: any) => void; // For standalone usage
  placeholder?: string;
  readOnly?: boolean;
  chipOptions?: ChipOption[];
  control?: any; // Optional control prop for react-hook-form
  showToolbar?: boolean;
  minHeight?: string;
  maxHeight?: string | null;
  maxScrollHeight?: string | null;
  error?: boolean;
  errorMessage?: string;
  noBorder?: boolean;
  customToolbar?: React.ReactNode; // Add custom toolbar prop
  toolbarPosition?: "top" | "bottom"; // Add toolbar position option
  headerContent?: string;
  footerContent?: string;
  pastingEnabled?: boolean;
  keyboardEnabled?: boolean;
  fixedAddIcon?: boolean; // Add this new prop
  hideHtmlInsertMenu?: boolean; // Add this new prop
  additionalSignatureComponent?: React.ReactNode;
  borderLessDesign?: boolean;
  backgroundColorEditor?: any;
  addIconDisabled?: boolean;
  externalChipOptions?: ChipOption[];
  onExternalChipOptionClick?: (chip: ChipOption) => void;
  hideItemsInToolbar?: ("left_align" | "center_align" | "right_align")[];
}

// Define the Delta type and custom blot for Quill
const Delta = Quill.import("delta");
const Embed = Quill.import("blots/embed");
const Image = Quill.import("formats/image");

// Define ChipBlot
ChipBlot.blotName = "chip";
ChipBlot.tagName = "span";
// Register the custom blot with Quill
Quill.register(ChipBlot, true);

//StyledImageBlot

StyledImageBlot.blotName = "styledImage";
StyledImageBlot.tagName = "img";
Quill.register(StyledImageBlot, true);

// Sizing is written inline because email clients strip stylesheet rules.
// Images keep their aspect ratio and are only ever scaled DOWN to fit inside
// this box, so a small logo is never stretched and a large screenshot never
// dominates the message.
const EMAIL_IMAGE_MAX_WIDTH = 300;
const EMAIL_IMAGE_MAX_HEIGHT = 200;

// Used when the intrinsic size can't be read (load error, dimensionless SVG).
const EMAIL_IMAGE_FALLBACK_STYLE = "max-width:100%;height:auto;";
// Bounds how long the upload overlay can stay up waiting on the probe.
const EMAIL_IMAGE_PROBE_TIMEOUT_MS = 4000;

const buildEmailImageStyle = (width: number) =>
  `width:${width}px;max-width:100%;height:auto;`;

/**
 * Reads an image's intrinsic size and returns the width it should render at:
 * scaled down to fit the email box, aspect ratio preserved, never upscaled.
 * Height is left to `height:auto`, so only the width is needed.
 * Resolves null when the intrinsic size can't be determined.
 */
const getScaledEmailImageWidth = (imageUrl: string): Promise<number | null> =>
  new Promise((resolve) => {
    // NOTE: the global `Image` constructor is shadowed by Quill's
    // `formats/image` import at module scope, so build the probe by hand.
    const probe = document.createElement("img");
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout>;
    const settle = (width: number | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      probe.onload = null;
      probe.onerror = null;
      resolve(width);
    };
    // Guard: if the probe never fires load OR error the insert must still
    // happen, otherwise the image silently disappears.
    timeoutId = setTimeout(() => settle(null), EMAIL_IMAGE_PROBE_TIMEOUT_MS);
    probe.onload = () => {
      const { naturalWidth, naturalHeight } = probe;
      if (!(naturalWidth && naturalHeight)) {
        settle(null);
        return;
      }
      const scale = Math.min(
        1,
        EMAIL_IMAGE_MAX_WIDTH / naturalWidth,
        EMAIL_IMAGE_MAX_HEIGHT / naturalHeight,
      );
      settle(Math.round(naturalWidth * scale));
    };
    probe.onerror = () => settle(null);
    probe.src = imageUrl;
  });

/** Filename minus its extension, for use as image alt text. */
const fileNameToAltText = (fileName: string) =>
  fileName.replace(/\.[^./\\]+$/, "").trim() || "Uploaded image";

//SignatureBlot

// SignatureBlot.blotName = "signature";
// SignatureBlot.tagName = "div";
// Quill.register(SignatureBlot, true);

// HTMLCodeBlot
HTMLCodeBlot.blotName = "htmlCode";
HTMLCodeBlot.tagName = "div";
HTMLCodeBlot.className = "html-code-blot";
Quill.register(HTMLCodeBlot, true);

interface CustomQuillEditorRef {
  sayHello: () => void;
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSignatureAction: (
    signatureType: unknown,
    signatureContent: string,
  ) => void;
  getEditor: () => ReactQuill | null;
  handleImageUrlInsert: (imageUrl: string, altText?: string) => Promise<void>;
  handleInsertHtmlCode: (htmlCode: string) => void;
}
const EMPTY_FILE_DATA: AttachmentData = {
  key: "",
  name: "",
  progress: 0,
  size: "",
  type: "",
  status: "Uploaded",
  responseData: null,
  file: null,
};

const QuillEditorWithChips = forwardRef<
  CustomQuillEditorRef,
  CustomQuillEditorProps
>(
  (
    {
      name,
      value,
      onChange,
      placeholder = "Type something...",
      readOnly = false,
      chipOptions = [
        // { id: "customerName", label: "Customer Name", value: "customer-name" },
      ],
      externalChipOptions = [],
      onExternalChipOptionClick = () => {},
      showToolbar = false,
      control,
      minHeight = "150px",
      maxHeight = null,
      error = false,
      errorMessage,
      noBorder = false,
      customToolbar,
      toolbarPosition = "top",
      headerContent = "",
      footerContent = "",
      pastingEnabled = true,
      keyboardEnabled = true,
      fixedAddIcon = true,
      hideHtmlInsertMenu = false,
      maxScrollHeight = null,
      additionalSignatureComponent,
      borderLessDesign = false,
      backgroundColorEditor = "",
      addIconDisabled = false,
      hideItemsInToolbar = [],
    }: CustomQuillEditorProps,
    ref,
  ) => {
    const quillRef = useRef<HTMLDivElement | null>(null);
    const editorRef = useRef<ReactQuill | null>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [plusPosition, setPlusPosition] = useState({ top: 0, left: 0 });
    const [fileData, setFileData] = useState<AttachmentData>(EMPTY_FILE_DATA);
    const [isEditButtonHovered, setIsEditButtonHovered] = useState(false);
    const [hoveredHtmlCodeId, setHoveredHtmlCodeId] = useState<string | null>(
      null,
    );
    const [hoveredHtmlCodeElement, setHoveredHtmlCodeElement] =
      useState<HTMLElement | null>(null);
    // Add a leaveTimer to prevent flickering
    const leaveTimerRef = useRef<number | null>(null);
    // Add a ref to track edit button interactions
    const isInteractingWithButtonRef = useRef(false);

    // State for HTML code editing modal
    const [htmlCodeModalOpen, setHtmlCodeModalOpen] = useState(false);
    const [htmlCode, setHtmlCode] = useState("");
    const [htmlCodeId, setHtmlCodeId] = useState("");
    const [currentHtmlCodeNode, setCurrentHtmlCodeNode] =
      useState<HTMLElement | null>(null);
    const [htmlCodeInputModalOpen, setHtmlCodeInputModalOpen] = useState(false);
    const [newHtmlCode, setNewHtmlCode] = useState("");

    // Use form context if control is not provided
    const formContext = useFormContext();
    const { setValue, getValues } = formContext || {};

    // Add a method to insert HTML code
    const handleInsertHtmlCode = useCallback((htmlCode: string) => {
      if (editorRef.current?.editor) {
        const editor = editorRef.current.editor;
        const range = editor.getSelection(true);
        const id = `html-${Date.now()}`;

        // Insert the HTML code at the current cursor position
        editor.insertEmbed(range.index, "htmlCode", {
          code: htmlCode,
          id: id,
        });

        // Ensure the HTML code is properly stored in the editor's content
        const delta = editor.getContents();
        if (delta && delta.ops) {
          delta.ops.forEach((op: any, index: number) => {
            if (
              op.insert &&
              op.insert.htmlCode &&
              op.insert.htmlCode.id === id
            ) {
              // Make sure the code is stored in the HTMLCodeBlot
              op.insert.htmlCode.code = htmlCode;
            }
          });

          // Apply the updated delta to the editor
          editor.setContents(delta);
        }

        // Move cursor after the HTML code
        editor.setSelection(range.index + 1, 0);
      }
    }, []);

    const theme = useTheme();
    useImperativeHandle(ref, () => ({
      sayHello: () => {
        console.log("Hello from CustomQuillEditor");
      },
      handleFileUpload,
      handleSignatureAction,
      getEditor: () => editorRef.current,
      handleImageUrlInsert,
      handleInsertHtmlCode,
    }));

    // Modify handleTextChange to only update position when needed
    const handleTextChange = (
      content: string,
      delta: any,
      source: string,
      editor: any,
    ) => {
      // Only update position if this is a user action (not programmatic change)
      if (source === "user") {
        updatePlusButtonPosition();
      }

      if (onChange) {
        onChange(content, delta, source, editor);
      } else if (name && (control || formContext)) {
        const formControl = control || formContext.control;
        formControl.setValue(name, content);
      }
    };

    // Determine the value to pass to ReactQuill
    const editorValue =
      name && (control || formContext) ? getValues(name) : value;

    // Track the last cursor position to avoid unnecessary updates
    const lastCursorPosition = useRef({ index: -1, length: 0 });

    // Throttle the position updates
    const updatePlusButtonPosition = useCallback(() => {
      if (!quillRef.current) return;
      const editor = editorRef.current?.getEditor();
      if (!editor) return;

      const selection = editor.getSelection();
      if (!selection) return;

      // Only update if cursor position actually changed
      if (
        selection.index === lastCursorPosition.current.index &&
        selection.length === lastCursorPosition.current.length
      ) {
        return;
      }

      lastCursorPosition.current = {
        index: selection.index,
        length: selection.length,
      };

      const bounds = editor.getBounds(selection.index);
      if (bounds) {
        const quillContainer = quillRef.current.querySelector(".ql-container");
        const containerRect = quillContainer?.getBoundingClientRect();
        const editorRect = quillRef.current.getBoundingClientRect();

        if (containerRect && editorRect) {
          // Position the plus button near the cursor
          setPlusPosition({
            top: bounds.top + bounds.height + 5,
            left: bounds.left + 5,
          });
        }
      }
    }, []);

    // Handle editor focus events
    const handleFocus = () => {
      setIsFocused(true);
      updatePlusButtonPosition();
    };

    const handleBlur = () => {
      // Use a timeout to prevent the plus button from disappearing
      // before the menu can be clicked
      setTimeout(() => {
        if (!anchorEl) {
          setIsFocused(false);
        }
      }, 200);
    };

    // Modify handleSelectionChange to throttle updates
    const handleSelectionChange = (range: any, source: string, editor: any) => {
      if (range && source === "user") {
        updatePlusButtonPosition();
      }
    };

    // Handle clicking the plus button
    const handlePlusClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      // Use the quillRef as the anchor element instead of the cursor position
      setAnchorEl(quillRef.current);
    };

    const handleChipInsert = (chip: ChipOption) => {
      const editor = editorRef.current?.getEditor();
      if (!editor) return;

      // Save the current selection
      const range = editor.getSelection(true);

      // Insert the chip at the current cursor position
      editor.insertEmbed(range.index, "chip", {
        id: chip.id,
        value: chip.value,
        label: chip.label,
        bgColor: chip.bgColor,
        icon: chip.icon,
        fontColor: chip.fontColor,
      });

      // Move cursor after the chip
      const newPosition = range.index + 1;
      editor.setSelection(newPosition, 0);

      // Close the menu
      setAnchorEl(null);

      // Focus back on the editor after a small delay to ensure the DOM is updated
      setTimeout(() => {
        if (editorRef.current) {
          // Focus the editor container
          const quillContainer = editorRef.current.getEditor().root;
          quillContainer.focus();

          // Set selection to the position after the chip
          editor.setSelection(newPosition, 0);
        }
      }, 50);
    };

    // Set up the editor when it's mounted
    useEffect(() => {
      const quill = editorRef.current?.getEditor();
      if (quill) {
        // Add custom key binding to handle deleting a chip as a single unit
        // Using '8' as a string instead of a number for the key property
        quill.keyboard.addBinding({ key: "8" }, (range: any, context: any) => {
          const [line, offset] = quill.getLine(range.index);
          // Check if we're at the beginning of a line or right after a chip
          if (
            offset === 0 ||
            quill.getContents(range.index - 1, 1).ops?.[0]?.insert?.chip
          ) {
            // If we're right after a chip, delete the entire chip
            if (quill.getContents(range.index - 1, 1).ops?.[0]?.insert?.chip) {
              quill.deleteText(range.index - 1, 1);
              return false;
            }
          }
          return true;
        });
      }
      // Fix the reference to the editor container element
      if (editorRef.current) {
        // Access the DOM element directly from the ReactQuill ref
        const editor: any = editorRef.current;
        // @ts-ignore - Accessing the internal DOM element, which TypeScript doesn't know about
        const editorElement: any =
          editor.getEditor().root.parentNode.parentNode;
        quillRef.current = editorElement;
      }
    }, []);
    // upload base64 image
    const handleFileUploadBase64 = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const file = event.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          const imageUrl = reader.result; // Get base64 URL
          if (editorRef.current?.editor) {
            // Get current content
            const currentContent =
              editorRef.current.editor.root.innerHTML || "";
            // Append image at the bottom
            const imageHtml = `<p><img src="${imageUrl}" alt="Uploaded Image" style="max-width: 100px; height: auto;"/></p>`;
            const newContent = currentContent + imageHtml;
            // Update the editor
            if (editorRef.current?.editor) {
              editorRef.current.editor.root.innerHTML = newContent;
            }
          }
        };
        reader.readAsDataURL(file); // Convert to base64
      }
    };

    // Runs after the upload completes. The overlay is held until the image
    // is actually in the document, because the insert probes the image for
    // its size first and clearing the status early would leave a gap with no
    // spinner and no image. `finally` guarantees the overlay clears even if
    // the insert throws; otherwise the editor would stay locked behind it.
    const insertUploadedImage = async (imageUrl: string, fileName: string) => {
      try {
        await handleImageUrlInsert(imageUrl, fileNameToAltText(fileName));
      } catch (error: unknown) {
        debugConsole("Image insert failed", error);
        toast.error("Failed to insert image. Please try again");
      } finally {
        setFileData((prev) => ({ ...prev, status: "Uploaded" }));
      }
    };

    // upload uploadedImage url image
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Check if a file was selected
      if (!file) {
        return;
      }
      // Validate file type (images only)
      const validImageTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/svg+xml",
      ];
      if (!validImageTypes.includes(file.type)) {
        toast.error(
          "Please upload a valid image file (JPEG, PNG, GIF, WEBP, SVG)",
        );
        return;
      }
      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        toast.error("Image size must be less than 10MB");
        return;
      }

      const formData = new FormData();
      formData?.append("file", file);
      const data: AttachmentData = {
        key: uuidv4() + "",
        name: file?.name || "",
        size: file?.size + "" || "",
        type: file?.type || "",
        status: "Uploading",
        progress: 0,
        file: file,
        responseData: null,
      };
      setFileData({ ...data });
      uploadEmailTemplateAttachment({
        formData,
      }).subscribe({
        next: async ({ response, status, percentage, controller }) => {
          if (status === "COMPLETE") {
            if (response?.success && response?.statusCode === 200) {
              setFileData((prev) => ({
                ...prev,
                progress: 100,
                responseData: response?.data,
              }));
              await insertUploadedImage(response?.data?.url, file.name);
            } else {
              setFileData((prev) => ({
                ...prev,
                status: "Error",
                progress: 0,
              }));
              toast.error("Failed to upload image. Please try again");
            }
          } else {
            setFileData((prev) => ({
              ...prev,
              status: "Uploading",
              progress: percentage,
            }));
          }
        },
      });
    };

    // Inserts via the `styledImage` blot (the same blot pasted <img> nodes
    // resolve to) so the saved HTML carries inline sizing. Email clients have
    // no .ql-editor CSS, so a bare <img> renders at its natural size.
    const handleImageUrlInsert = async (
      imageUrl: string,
      altText = "Uploaded image",
    ) => {
      if (!editorRef.current?.editor) {
        debugConsole("Editor reference is not available");
        return;
      }
      const width = await getScaledEmailImageWidth(imageUrl);

      // Read the caret AFTER the probe. The company-logo path involves no
      // upload, so the editor stays editable while the probe runs and a caret
      // captured earlier could be stale. (The upload path keeps the editor
      // readOnly until after this insert, so it is unaffected either way.)
      const editor = editorRef.current?.editor;
      if (!editor) return;
      const range = editor.getSelection();
      const index = range ? range.index : editor.getLength() - 1;

      // Source is left at the "api" default on purpose: Quill drops
      // "user"-sourced edits on a disabled editor, and react-quill fires
      // onChange either way, so the form value still updates.
      editor.insertEmbed(index, "styledImage", {
        src: imageUrl,
        alt: altText,
        style: width ? buildEmailImageStyle(width) : EMAIL_IMAGE_FALLBACK_STYLE,
      });
      editor.setSelection(index + 1, 0);
    };

    const handleSignatureAction = (
      signatureType: any,
      signatureContent: string,
    ) => {
      if (!editorRef.current?.editor) return;
      const editor = editorRef.current.editor;
      const editorLength = editor.getLength();

      const delta = new Delta()
        .retain(editorLength - 1)
        .insert(editorLength > 1 ? "\n" : "") // Conditional newline before
        .insert(
          { signature: { content: signatureContent, id: `sig-${Date.now()}` } },
          { alt: "Signature" },
        )
        .insert("\n"); // Newline after

      editor.updateContents(delta, "user");

      const newPosition = editor.getLength();
      editor.setSelection(newPosition, 0);
    };

    // Update your clipboard matcher to correctly handle pasted content
    useEffect(() => {
      const quill = editorRef.current?.getEditor();
      if (quill) {
        quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
          // console.log("Clipboard node:", node);
          // console.log("Clipboard delta:", delta);
          // Check if the delta incorrectly identified content as a chip
          if (
            delta.ops?.some(
              (op) =>
                op.insert?.chip &&
                (!op.insert.chip.value ||
                  !op.insert.chip.label ||
                  !op.insert.chip.id),
            )
          ) {
            // Extract the text content from the node
            const textContent = node.textContent || "";
            // Replace the invalid chip delta with plain text
            return new Delta().insert(textContent);
          }
          return delta;
        });
      }
    }, []);

    // Disable all keyboard input except "Enter", "Space", and "Backspace"
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!keyboardEnabled) {
        if (e.key !== "Enter" && e.key !== " " && e.key !== "Backspace") {
          e.preventDefault();
        }
      }
    };

    // Handle HTML code editing
    const handleHtmlCodeEdit = useCallback((event: CustomEvent) => {
      const { id, code } = event.detail;
      setHtmlCodeId(id);
      setHtmlCode(code);
      setHtmlCodeModalOpen(true);

      // Find the node in the editor
      if (editorRef.current?.editor) {
        const editor = editorRef.current.editor;
        const delta = editor.getContents();
        let nodeIndex = -1;
        let totalLength = 0;

        // Find the index of the HTML code blot
        if (delta && delta.ops) {
          delta.ops.forEach((op: any) => {
            if (
              op.insert &&
              op.insert.htmlCode &&
              op.insert.htmlCode.id === id
            ) {
              nodeIndex = totalLength;
            }
            // Add the length of this operation to the total
            if (typeof op.insert === "string") {
              totalLength += op.insert.length;
            } else if (
              op.insert &&
              (op.insert.htmlCode ||
                op.insert.chip ||
                op.insert.image ||
                op.insert.styledImage)
            ) {
              totalLength += 1;
            }
          });
        }

        if (nodeIndex !== -1) {
          // Get the DOM node
          const [node] = editor.getLeaf(nodeIndex);
          if (node && node.domNode) {
            setCurrentHtmlCodeNode(node.domNode);
          }
        }
      }
    }, []);

    const minifyAndSanitizeHtmlCode = (htmlCode: string) => {
      const minifiedHtmlCode = minifyHtml(htmlCode);
      return minifiedHtmlCode;
    };

    // Save HTML code changes
    const handleSaveHtmlCode = useCallback(() => {
      if (editorRef.current?.editor) {
        const editor = editorRef.current.editor;

        console.log("🚀 ~ handleSaveHtmlCode ~ htmlCode:", htmlCode);
        const sanitizedHtml = minifyAndSanitizeHtmlCode(htmlCode);
        console.log("🚀 ~ handleSaveHtmlCode ~ sanitizedHtml:", sanitizedHtml);
        // Find the node again to ensure we have the latest reference
        const delta = editor.getContents();
        let nodeIndex = -1;
        let totalLength = 0;

        // Find the index of the HTML code blot
        if (delta && delta.ops) {
          delta.ops.forEach((op: any) => {
            if (
              op.insert &&
              op.insert.htmlCode &&
              op.insert.htmlCode.id === htmlCodeId
            ) {
              nodeIndex = totalLength;
            }
            // Add the length of this operation to the total
            if (typeof op.insert === "string") {
              totalLength += op.insert.length;
            } else if (
              op.insert &&
              (op.insert.htmlCode ||
                op.insert.chip ||
                op.insert.image ||
                op.insert.styledImage)
            ) {
              totalLength += 1;
            }
          });
        }

        if (nodeIndex !== -1) {
          // Get the current DOM node
          const [node] = editor.getLeaf(nodeIndex);
          if (node && node.domNode) {
            // Update the DOM node's data-code attribute
            node.domNode.setAttribute("data-code", sanitizedHtml);
            // Update the preview
            node.domNode.innerHTML = sanitizedHtml;
          }

          // Update the Quill editor's content
          const updatedDelta = editor.getContents();
          if (updatedDelta && updatedDelta.ops) {
            updatedDelta.ops.forEach((op: any) => {
              if (
                op.insert &&
                op.insert.htmlCode &&
                op.insert.htmlCode.id === htmlCodeId
              ) {
                op.insert.htmlCode.code = sanitizedHtml;
              }
            });

            // Apply the updated delta to the editor
            editor.setContents(updatedDelta);
          }
        }

        // Close the modal
        setHtmlCodeModalOpen(false);
      }
    }, [htmlCode, htmlCodeId]);

    const handleDeleteHtmlCode = useCallback(() => {
      const editor = editorRef.current?.editor;
      if (editor) {
        // Find the index of the HTML code blot with matching ID
        const delta = editor.getContents();
        let nodeIndex = -1;
        let totalLength = 0;

        // Search through delta operations for the blot with matching ID
        if (delta && delta.ops) {
          delta.ops.forEach((op: any) => {
            if (
              op.insert &&
              op.insert.htmlCode &&
              op.insert.htmlCode.id === htmlCodeId
            ) {
              nodeIndex = totalLength;
            }
            // Add the length of this operation to the total
            if (typeof op.insert === "string") {
              totalLength += op.insert.length;
            } else if (
              op.insert &&
              (op.insert.htmlCode ||
                op.insert.chip ||
                op.insert.image ||
                op.insert.styledImage)
            ) {
              totalLength += 1;
            }
          });
        }

        // If found, delete the blot
        if (nodeIndex !== -1) {
          editor.deleteText(nodeIndex, 1);
        }
      }
    }, [htmlCodeId]);

    // Set up event listener for HTML code editing
    useEffect(() => {
      const editorElement = editorRef.current?.editor?.root;
      if (editorElement) {
        editorElement.addEventListener(
          "htmlCodeEdit",
          handleHtmlCodeEdit as EventListener,
        );

        return () => {
          editorElement.removeEventListener(
            "htmlCodeEdit",
            handleHtmlCodeEdit as EventListener,
          );
        };
      }
    }, [handleHtmlCodeEdit]);

    // Handle opening the HTML code input modal from ChipMenu
    const handleOpenHtmlCodeInputModal = useCallback(() => {
      setHtmlCodeInputModalOpen(true);
      setAnchorEl(null); // Close the ChipMenu
    }, []);

    // Handle saving new HTML code from the input modal
    const handleSaveNewHtmlCode = useCallback(() => {
      if (newHtmlCode.trim()) {
        console.log("🚀 ~ handleSaveNewHtmlCode ~ newHtmlCode:", newHtmlCode);

        const sanitizedHtml = minifyAndSanitizeHtmlCode(newHtmlCode);

        console.log(
          "🚀 ~ handleSaveNewHtmlCode ~ sanitizedHtml:",
          sanitizedHtml,
        );
        // Insert the HTML code
        handleInsertHtmlCode(sanitizedHtml);

        // Clear the input and close the modal
        setNewHtmlCode("");
        setHtmlCodeInputModalOpen(false);
      }
    }, [newHtmlCode, handleInsertHtmlCode]);

    return (
      <Stack gap="4px">
        <Box
          sx={{
            position: "relative",
          }}
        >
          {/* Loading overlay */}
          {fileData?.status === "Uploading" && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {/* Render custom toolbar at the top if position is 'top' */}
          {showToolbar && toolbarPosition === "top" && (
            <Box sx={{ mb: 1 }}>
              {customToolbar || (
                <DefaultToolbar
                  editorRef={editorRef}
                  borderLessDesign={borderLessDesign}
                  hideItemsInToolbar={hideItemsInToolbar}
                />
              )}
            </Box>
          )}
          <Box
            sx={{
              position: "relative",
              maxHeight: maxScrollHeight || "1000px",
              overflow: "scroll",
              border: error
                ? `1px solid ${theme.palette.error.main}`
                : borderLessDesign
                  ? `1px solid ${theme.palette.darkBg.medium}`
                  : "none",
              marginBottom: borderLessDesign ? "16px" : "",
              borderRadius: borderLessDesign ? "12px" : "",
            }}
          >
            {headerContent && (
              <Box
                sx={quilStyle({
                  minHeight: "0px",
                  showToolbar,
                  noBorder: false,
                  backgroundColor: "#FAFAF9",
                  lineHeight: "26px",
                })}
              >
                <ReactQuill
                  value={headerContent}
                  onChange={() => {}}
                  onChangeSelection={handleSelectionChange}
                  placeholder={""}
                  readOnly={true}
                  theme="snow"
                  modules={{
                    toolbar: false,
                  }}
                />
              </Box>
            )}

            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  backgroundColor: readOnly
                    ? "white"
                    : theme.palette.neutral["005"],
                }}
              >
                <Box
                  sx={{
                    ...quilStyle({
                      minHeight,
                      showToolbar,
                      noBorder: noBorder,
                      backgroundColorEditor: backgroundColorEditor,
                      ...(fileData?.status === "Uploading"
                        ? { opacity: 0.5 }
                        : { display: "flex" }),
                      width: fixedAddIcon && !addIconDisabled ? "96%" : "100%",
                      lineHeight: "26px",
                    }),
                    "& .html-code-blot": readOnly
                      ? {}
                      : {
                          transition: "all 0.3s ease",
                          border: `1px solid ${theme.palette.neutral["015"]}`,
                          "&:hover": {
                            cursor: "pointer",
                            backgroundColor: theme.palette.neutral["010"],
                          },
                        },
                  }}
                >
                  <ReactQuill
                    ref={editorRef}
                    value={editorValue || ""}
                    onChange={handleTextChange}
                    onChangeSelection={handleSelectionChange}
                    placeholder={placeholder}
                    readOnly={readOnly || fileData?.status === "Uploading"}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    theme="snow"
                    style={{}}
                    onKeyDown={handleKeyDown}
                    modules={{
                      toolbar: false,
                    }}
                  />
                </Box>
                <Box
                  sx={(theme) => ({
                    ...(readOnly
                      ? {}
                      : {
                          // borderTop: "1px solid #EDEDED",
                          borderRight: "1px solid #EDEDED",
                          // borderBottom: "1px solid #EDEDED",
                        }),
                    padding: "2px",
                    position: "sticky",
                    top: "0", // Stick to the top of the container
                    alignSelf: "flex-start", // Align to the top
                    zIndex: 1, // Ensure it stays above other content
                  })}
                >
                  {fixedAddIcon &&
                    !readOnly &&
                    !addIconDisabled &&
                    fileData?.status !== "Uploading" && (
                      <IconButton
                        size="small"
                        sx={{
                          // position: "absolute",
                          // top: plusPosition.top - 23,
                          // top: 8,
                          // right: 8,
                          // zIndex: 10,
                          backgroundColor: "white",
                          color: "black",
                          // "&:hover": { backgroundColor: "#EDEDED" },
                          "&:hover": { backgroundColor: "white" },
                          width: "23px",
                          height: "23px",
                          border: "1px solid #EDEDED",
                          borderRadius: "6px",
                        }}
                        onClick={handlePlusClick}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    )}
                </Box>
              </Box>

              {/* Original floating AddIcon button (only shown when fixedAddIcon is false) */}
              {isFocused &&
                !readOnly &&
                !fixedAddIcon &&
                fileData?.status !== "Uploading" && (
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: plusPosition.top - 23,
                      left: plusPosition.left,
                      zIndex: 10,
                      backgroundColor: "transparent", // Make background transparent
                      color: "rgba(0, 0, 0, 0.3)", // Make icon slightly faded
                      width: "20px",
                      height: "20px",
                      border: "1px solid transparent", // Hide border by default
                      borderRadius: "6px",
                      transition: "all 0.2s ease", // Smooth transition
                      opacity: 0.7, // Slightly transparent by default
                      "&:hover": {
                        backgroundColor: "white", // Show white background on hover
                        color: "black", // Make icon fully visible on hover
                        border: "1px solid #EDEDED", // Show border on hover
                        opacity: 1, // Fully opaque on hover
                      },
                    }}
                    onClick={handlePlusClick}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                )}

              {footerContent && (
                <Box
                  sx={quilStyle({
                    minHeight: "0px",
                    showToolbar,
                    noBorder: false,
                    backgroundColor: "#FAFAF9",
                    lineHeight: "26px",
                  })}
                >
                  <ReactQuill
                    value={footerContent}
                    onChange={() => {}}
                    onChangeSelection={handleSelectionChange}
                    placeholder={""}
                    readOnly={true}
                    theme="snow"
                    modules={{
                      toolbar: false,
                    }}
                  />
                </Box>
              )}
            </Box>

            {additionalSignatureComponent}

            <ChipMenu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              chipOptions={chipOptions}
              externalChipOptions={externalChipOptions}
              onExternalChipOptionClick={onExternalChipOptionClick}
              handleChipInsert={handleChipInsert}
              handleOpenHtmlCodeInputModal={handleOpenHtmlCodeInputModal}
              hideHtmlInsertMenu={hideHtmlInsertMenu}
            />
          </Box>

          {/* HTML Code Editing Modal */}
          {htmlCodeModalOpen && (
            <HtmlCodeModal
              open={htmlCodeModalOpen}
              onClose={() => setHtmlCodeModalOpen(false)}
              title="Edit HTML code"
              primaryButtonText="Save"
              secondaryButtonText="Delete this block"
              onPrimaryButtonClick={handleSaveHtmlCode}
              onSecondaryButtonClick={() => {
                setHtmlCodeModalOpen(false);
                handleDeleteHtmlCode();
              }}
              htmlCode={htmlCode}
              onHtmlCodeChange={setHtmlCode}
              chipOptions={chipOptions}
              onChipInsert={handleChipInsert}
            />
          )}

          {/* HTML Code Input Modal (from ChipMenu) */}
          {htmlCodeInputModalOpen && (
            <HtmlCodeModal
              open={htmlCodeInputModalOpen}
              onClose={() => setHtmlCodeInputModalOpen(false)}
              title="Insert HTML code"
              primaryButtonText="Insert"
              secondaryButtonText="Cancel"
              onPrimaryButtonClick={handleSaveNewHtmlCode}
              onSecondaryButtonClick={() => setHtmlCodeInputModalOpen(false)}
              htmlCode={newHtmlCode}
              onHtmlCodeChange={setNewHtmlCode}
              chipOptions={chipOptions}
              onChipInsert={handleChipInsert}
            />
          )}
        </Box>
        {error && (
          <Typography ml="14px" variant="body-small" color="error">
            {errorMessage || "Required"}
          </Typography>
        )}
      </Stack>
    );
  },
);

const ChipMenu = ({
  anchorEl,
  open,
  onClose,
  chipOptions,
  externalChipOptions,
  onExternalChipOptionClick,
  handleChipInsert,
  handleOpenHtmlCodeInputModal,
  hideHtmlInsertMenu,
}: {
  anchorEl: any;
  open: boolean;
  onClose: () => void;
  chipOptions: ChipOption[];
  externalChipOptions: ChipOption[];
  onExternalChipOptionClick: (chip: ChipOption) => void;
  handleChipInsert: (chip: ChipOption) => void;
  handleOpenHtmlCodeInputModal: () => void;
  hideHtmlInsertMenu: boolean;
}) => {
  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const filteredChipOptions = chipOptions.filter((chip) =>
    chip.label.toLowerCase().includes(searchValue.toLowerCase()),
  );
  const filteredExternalChipOptions = externalChipOptions.filter((chip) =>
    chip.label.toLowerCase().includes(searchValue.toLowerCase()),
  );
  // Handle keydown to prevent menu navigation
  const handleKeyDown = (e: any) => {
    e.stopPropagation(); // Prevents the Menu from intercepting the keypress
  };

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  const handleChipClick = (chip: ChipOption) => {
    handleChipInsert(chip);
    onClose();
    setSearchValue("");
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={() => {
        onClose(), setSearchValue("");
      }}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      TransitionProps={{
        onEntered: () => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        },
      }}
    >
      <Box
        sx={{
          p: "0px 8px",
          position: "sticky",
          top: 0,
          backgroundColor: "background.paper",
          zIndex: 1,
        }}
      >
        <SearchInput
          inputRef={searchInputRef}
          value={searchValue}
          onChange={(e) => {
            e.preventDefault();
            setSearchValue(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          name="search"
          placeholder="Search"
        />
        <Typography variant="body-small" sx={{ p: "6px 4px 0 4px" }}>
          Variables
        </Typography>
      </Box>
      <Box sx={{ maxHeight: "300px", overflow: "auto" }}>
        {!hideHtmlInsertMenu && (
          <MenuItem
            sx={{ p: "6px 8px" }}
            onClick={handleOpenHtmlCodeInputModal}
          >
            <Box
              sx={{
                borderRadius: "4px",
                p: "2px",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "24px",
                  gap: "8px",
                  border: "1px solid #E0E0E0",
                  borderRadius: "4px",
                  padding: "2px 6px",
                  backgroundColor: "rgba(237, 237, 237, 1)",
                }}
              >
                <Box
                  sx={{
                    width: "18px",
                    height: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#1976d2",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="16 18 22 12 16 6"></polyline>
                    <polyline points="8 6 2 12 8 18"></polyline>
                  </svg>
                </Box>
                <Typography variant="body-small">Insert HTML code</Typography>
              </Box>
            </Box>
          </MenuItem>
        )}

        {filteredExternalChipOptions.map((chip) => (
          <MenuItem
            key={chip.id}
            sx={{ p: "6px 8px" }}
            onClick={() => {
              onExternalChipOptionClick?.(chip);
              onClose();
            }}
          >
            <Box
              sx={{
                borderRadius: "4px",
                p: "2px",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "24px",
                  gap: "8px",
                  border: "1px solid #E0E0E0",
                  borderRadius: "4px",
                  padding: "2px 6px",
                  backgroundColor: "rgba(237, 237, 237, 1)",
                }}
              >
                <Typography variant="body-small">{chip?.label}</Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
        {filteredChipOptions.map((chip) => (
          <MenuItem
            key={chip.id}
            sx={{ p: "6px 8px" }}
            onClick={() => handleChipClick(chip)}
          >
            <Box
              sx={{
                borderRadius: "4px",
                p: "2px",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "24px",
                  gap: "8px",
                  border: "1px solid #E0E0E0",
                  borderRadius: "4px",
                  padding: "2px 6px",
                  backgroundColor: "rgba(237, 237, 237, 1)",
                }}
              >
                <Typography variant="body-small">{chip?.label}</Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Box>
    </Menu>
  );
};

// Create the HtmlCodeModal component
const HtmlCodeModal = ({
  open,
  onClose,
  title,
  primaryButtonText,
  secondaryButtonText,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  htmlCode,
  onHtmlCodeChange,
  chipOptions,
  onChipInsert,
  sx,
}: HtmlCodeModalProps) => {
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const htmlCodeRef = useRef<AceEditor>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Handle keydown to prevent menu navigation
  const handleKeyDown = (e: any) => {
    e.stopPropagation(); // Prevents the Menu from intercepting the keypress
  };

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  // Handle plus button click
  const handlePlusClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onModalClose = () => {
    setAnchorEl(null);
    setSearchValue("");
    onHtmlCodeChange("");
    onClose();
  };

  // Handle chip insertion
  const handleChipInsert = (chip: ChipOption) => {
    const chipHtml = `<span class="dd-template-chip" data-value="${
      chip.value
    }" data-label="${chip.label}" data-id="${chip.id}" id="${
      chip.id
    }"><span style="display: inline-flex; align-items: center; height: 24px; gap: 8px; border: 1px solid rgba(234, 214, 255, 1); border-radius: 4px; padding: 2px 6px; background-color: ${
      chip.bgColor || "rgba(241, 232, 255, 1)"
    }; cursor: default; white-space: nowrap; font-size: 12px; vertical-align: middle; color: rgba(102, 0, 250, 1);">${
      chip.label
    }</span></span>`;

    if (htmlCodeRef.current) {
      const editor = htmlCodeRef.current.editor;
      const cursorPos = editor.getCursorPosition();

      // Insert the chip HTML at the current cursor position
      editor.insert(chipHtml);

      setTimeout(() => {
        editor.focus();
      }, 0);
    }
    setAnchorEl(null);
    setSearchValue("");
  };

  return (
    <Drawer
      open={open}
      onClose={onModalClose}
      aria-labelledby="html-code-modal-title"
      aria-describedby="html-code-modal-description"
      primaryButtonText={primaryButtonText}
      secondaryButtonText={secondaryButtonText}
      onPrimaryButtonClick={onPrimaryButtonClick}
      onSecondaryButtonClick={onSecondaryButtonClick}
      isPrimaryButtonDisabled={htmlCode.length === 0}
      title={title}
      sx={{
        "& .MuiModal-paper": {
          height: "min(650px, 85vh)",
          ...sx,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={{ p: "16px", width: "min(900px, 100vw)", height: "100%" }}>
        <Stack
          direction={"row"}
          sx={{
            position: "relative",
            width: "100%",
            gap: "4px",
            height: "100%",
            overflow: "hidden",
            flex: 1,
            p: "4px 8px",
            backgroundColor: theme.palette.neutral["005"],
            borderRadius: "8px",
            border: `1px solid ${theme.palette.neutral["010"]}`,
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              display: "flex",
              height: "100%",
              flexDirection: "column",
              overflow: "scroll",
              "& .ace_editor": {
                backgroundColor: "transparent",
              },
            }}
          >
            {/* <TextArea
              name="html-code-modal-description"
              id="html-code-modal-description"
              value={htmlCode}
              onChange={(e) => {
                onHtmlCodeChange(e.target.value);
              }}
              placeholder="Enter your HTML code here"
              minRows={10}
              sx={{
                p: "8px",
                width: "100%",
                minHeight: "fit-content",
                resize: "none",
                border: "none !important",
                overflowY: "scroll",
              }}
            /> */}

            <AceEditor
              name="html-code-modal-description"
              ref={htmlCodeRef}
              placeholder="Enter your HTML code here"
              mode="html"
              theme="github"
              onChange={(value) => {
                onHtmlCodeChange(value);
              }}
              fontSize={12}
              lineHeight={19}
              showPrintMargin={false}
              showGutter={false}
              highlightActiveLine={false}
              value={htmlCode}
              height="100%"
              width="100%"
              setOptions={{
                enableBasicAutocompletion: false,
                enableLiveAutocompletion: false,
                enableSnippets: true,
                enableMobileMenu: true,
                showLineNumbers: true,
                tabSize: 2,
              }}
            />
          </Box>
          {chipOptions && chipOptions.length > 0 && (
            <>
              <Divider
                sx={{
                  borderColor: theme.palette.neutral["010"],
                  m: "0px",
                }}
                orientation="vertical"
                flexItem
              />
              <IconButton
                sx={{
                  backgroundColor: "white",
                  color: "black",
                  "&:hover": { backgroundColor: "#EDEDED" },
                  width: "24px",
                  height: "24px",
                  border: "1px solid #EDEDED",
                  borderRadius: "6px",
                  alignSelf: "flex-start",
                }}
                onClick={handlePlusClick}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </>
          )}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => {
              setAnchorEl(null);
              setSearchValue("");
            }}
            TransitionProps={{
              onEntered: () => {
                if (searchInputRef.current) {
                  searchInputRef.current.focus();
                }
              },
            }}
          >
            <Box sx={{ p: "0px 8px" }}>
              <SearchInput
                inputRef={searchInputRef}
                value={searchValue}
                onChange={(e) => {
                  e.preventDefault();
                  setSearchValue(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                name="search"
                placeholder="Search"
              />
              <Typography variant="body-small" sx={{ p: "6px 4px 0 4px" }}>
                Variables
              </Typography>
            </Box>
            {chipOptions
              .filter((chip) =>
                chip.label.toLowerCase().includes(searchValue.toLowerCase()),
              )
              .map((chip) => (
                <MenuItem
                  key={chip.id}
                  sx={{ p: "6px 8px" }}
                  onClick={() => handleChipInsert(chip)}
                >
                  <Box
                    sx={{
                      borderRadius: "4px",
                      p: "2px",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        height: "24px",
                        gap: "8px",
                        border: "1px solid #E0E0E0",
                        borderRadius: "4px",
                        padding: "2px 6px",
                        backgroundColor: "rgba(237, 237, 237, 1)",
                      }}
                    >
                      <LeftRightArrowIconChip
                        sx={{ width: "18px", height: "18px" }}
                      />
                      <Typography variant="body-small">{chip.label}</Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
          </Menu>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default QuillEditorWithChips;
